import Image from "next/image"
import { useRouter } from "next/router"
import React, { useEffect } from "react"

import Datadog from "@bounce/datadog"
import { useTranslation, DEFAULT_LANGUAGE } from "@bounce/i18n"
import { Button, Link } from "@bounce/web-components"

import { Layout } from "../../../components/Generic/Layout/Layout"

import catInBoxImage from "@bounce/assets/images/cat-box.png"

export type ErrorScreenProps = {
  statusCode: number
}

const useError = (statusCode: number) => {
  const { t } = useTranslation()

  switch (statusCode) {
    case 500:
      return {
        title: t("page.error.500Title", "Internal server error"),
        subTitle: t("page.error500.SubTitle", "We’re having trouble on our server."),
      }
    default:
      return {
        title: t("page.error.404Title", "Page not found"),
        subTitle: t("page.error404.subTitle", "The page you’re looking for does not exist."),
      }
  }
}

const ErrorScreen = ({ statusCode }: ErrorScreenProps) => {
  const router = useRouter()
  const error = useError(statusCode)
  const { t } = useTranslation()

  useEffect(() => {
    if (!statusCode) return

    const error = new Error(`page response with a ${statusCode} code`)
    void Datadog.addError(error, {
      pageName: router.asPath,
    })
  }, [router.asPath, statusCode])

  return (
    <Layout headProps={{ noIndex: true }} languages={[DEFAULT_LANGUAGE]}>
      <div className="mx-auto flex w-full max-w-[900px] flex-col items-center justify-center px-6 pt-12 pb-section md:flex-row md:items-start md:justify-start md:pt-14">
        <Image
          alt=""
          width={312}
          height={312}
          loading="eager"
          priority
          className="max-w-full object-contain"
          src={catInBoxImage}
        />
        <div className="mt-4 flex flex-1 flex-col md:mt-24 md:pl-12">
          <h1 className="title1 w-full break-words text-center text-black md:text-left">{error.title}</h1>
          <p className="paragraph1 mt-3 w-full px-4 text-center md:mt-4 md:px-0 md:text-left">{error.subTitle}</p>
          <Button as={Link} variant="primary" color="primary" href="/" className="mt-8 w-full md:mt-11 md:w-fit">
            {t("page.error.backToHomeButton", "Back to home")}
          </Button>
        </div>
      </div>
    </Layout>
  )
}

export default ErrorScreen
